import type {
  FilterAttributes,
  FilterDefinition,
  FilterSchema,
  FilterToken,
  LabelMap,
} from './types'

/** Multi-value filters travel as one comma separated parameter, as the task filters already do. */
const VALUE_SEPARATOR = ','

export function definitionFor(schema: FilterSchema, key: string): FilterDefinition | undefined {
  return schema.definitions.find((definition) => definition.key === key)
}

export function freeTextDefinition(schema: FilterSchema): FilterDefinition | undefined {
  return schema.definitions.find((definition) => definition.kind === 'text' && definition.freeText)
}

/**
 * Tokens -> query parameters. Only parameters with a value appear, so a cleared filter leaves no
 * empty `?filters[name]=` behind.
 */
export function tokensToQuery(schema: FilterSchema, tokens: FilterToken[]): Record<string, string> {
  const query: Record<string, string> = {}

  schema.definitions.forEach((definition) => {
    const values = tokens
      .filter((token) => token.key === definition.key)
      .map((token) => token.value)
      .filter((value) => value !== '')
    if (!values.length) {
      return
    }
    query[definition.param] = definition.multiple
      ? values.join(VALUE_SEPARATOR)
      : String(values[values.length - 1])
  })

  return query
}

/**
 * Query parameters -> tokens. Unknown parameters are ignored, so an unrelated query string (a
 * `page`, a redirect target) survives untouched.
 */
export function queryToTokens(
  schema: FilterSchema,
  query: Record<string, unknown>,
  labels: LabelMap = {},
): FilterToken[] {
  const tokens: FilterToken[] = []

  schema.definitions.forEach((definition) => {
    const raw = paramValue(query, definition)
    if (raw === undefined || raw === null || raw === '') {
      return
    }
    // vue-router hands back an array when a parameter appears twice; the last one wins, the same
    // way a duplicated parameter behaves server side.
    const flat = Array.isArray(raw) ? raw[raw.length - 1] : raw
    if (flat === undefined || flat === null || flat === '') {
      return
    }
    // A boolean filter is only "on" for its own value. Any other value — `hide_resolved=true`,
    // which is the default and means the toggle is off — leaves the filter without a token.
    if (definition.kind === 'boolean' && String(flat) !== (definition.onValue ?? 'true')) {
      return
    }
    const values = definition.multiple ? String(flat).split(VALUE_SEPARATOR) : [String(flat)]
    values
      .map((value) => value.trim())
      .filter((value) => value !== '')
      .forEach((value) => {
        tokens.push({
          key: definition.key,
          value,
          label: labels[definition.key]?.[value] ?? value,
        })
      })
  })

  return tokens
}

/** The value of a filter's parameter; a former name only counts when the current one is absent. */
export function paramValue(query: Record<string, unknown>, definition: FilterDefinition): unknown {
  const current = query[definition.param]
  if (current !== undefined && current !== null && current !== '') {
    return current
  }
  for (const legacy of definition.legacyParams ?? []) {
    const value = query[legacy]
    if (value !== undefined && value !== null && value !== '') {
      return value
    }
  }
  return current
}

/**
 * Tokens -> the `filterAttributes` object the api functions take. Every definition is present, an
 * absent filter as null, so a cleared filter actually clears server side instead of keeping the
 * previous value.
 */
export function tokensToFilterAttributes(
  schema: FilterSchema,
  tokens: FilterToken[],
): FilterAttributes {
  const query = tokensToQuery(schema, tokens)
  const attributes: FilterAttributes = {}

  schema.definitions.forEach((definition) => {
    attributes[definition.key] = query[definition.param] ?? null
  })

  return attributes
}

/**
 * Drops tokens of filters that are currently hidden.
 *
 * A hidden filter must not keep narrowing the list — that is what the old task filter did by only
 * reading the visible one of the two status filters, and it is the one behaviour a naive migration
 * would silently lose.
 */
export function withoutHiddenTokens(schema: FilterSchema, tokens: FilterToken[]): FilterToken[] {
  return tokens.filter((token) => {
    const definition = definitionFor(schema, token.key)
    return !definition?.visibleWhen || definition.visibleWhen(tokens)
  })
}

/** True when two token lists describe the same filter, regardless of order. */
export function sameTokens(schema: FilterSchema, a: FilterToken[], b: FilterToken[]): boolean {
  return JSON.stringify(tokensToQuery(schema, a)) === JSON.stringify(tokensToQuery(schema, b))
}
